import { useState, type ReactNode } from 'react';
import {
    AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItemButton, ListItemIcon,
    ListItemText, Box, Avatar, Menu, MenuItem, Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import PlaceIcon from '@mui/icons-material/Place';
import ReviewsIcon from '@mui/icons-material/Reviews';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GavelIcon from '@mui/icons-material/Gavel';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PaymentsIcon from '@mui/icons-material/Payments';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EuroIcon from '@mui/icons-material/Euro';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FlagIcon from '@mui/icons-material/Flag';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';

const drawerWidth = 240;

export const NAV_ITEMS = [
    { label: 'Pārskats',      path: '/',            icon: <DashboardIcon /> },
    { label: 'Lietotāji',     path: '/users',       icon: <PeopleIcon /> },
    { label: 'Vietas',        path: '/places',      icon: <PlaceIcon /> },
    { label: 'Atsauksmes',    path: '/reviews',     icon: <ReviewsIcon /> },
    { label: 'Foto moderācija', path: '/photos',    icon: <PhotoLibraryIcon /> },
    { label: 'Vietu moderācija', path: '/moderation/places', icon: <GavelIcon /> },
    { label: 'Teksta moderācija', path: '/moderation/reviews', icon: <RateReviewIcon /> },
    { label: 'Ārējo atsauksmju moderācija', path: '/moderation/external-reviews', icon: <RateReviewIcon /> },
    { label: 'Ārējo foto moderācija', path: '/moderation/external-review-photos', icon: <PhotoLibraryIcon /> },
    { label: 'Satura ziņojumi', path: '/moderation/reports', icon: <FlagIcon /> },
    { label: 'Ignorētās ārējās vietas', path: '/external-ignored', icon: <VisibilityOffIcon /> },
    { label: 'Maksājumi',     path: '/payments',    icon: <PaymentsIcon /> },
    { label: 'Cenas',         path: '/prices',      icon: <EuroIcon /> },
    { label: 'Klasifikatori', path: '/classifiers', icon: <CategoryIcon /> },
    { label: 'Rīgas domes tualetes', path: '/external/riga-dome', icon: <CloudDownloadIcon /> },
];

export function AppLayout({ children }: { children: ReactNode }) {
    const { user, signOut } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const location = useLocation();

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
                    ml: drawerOpen ? `${drawerWidth}px` : 0,
                    transition: 'width .2s, margin-left .2s',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => setDrawerOpen(!drawerOpen)}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Unicnet Admin
                    </Typography>
                    <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <Avatar
                            src={user?.photoURL ?? undefined}
                            sx={{ width: 32, height: 32 }}
                        >
                            {user?.email?.charAt(0).toUpperCase()}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                    >
                        <MenuItem disabled>{user?.email}</MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                setMenuAnchor(null);
                                void signOut();
                            }}
                        >
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            Izrakstīties
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="persistent"
                open={drawerOpen}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
                }}
            >
                <Toolbar>
                    <Typography variant="h6" fontWeight={600}>Unicnet</Typography>
                </Toolbar>
                <Divider />
                <List>
                    {NAV_ITEMS.map((item) => {
                        const selected =
                            item.path === '/'
                                ? location.pathname === '/'
                                : location.pathname.startsWith(item.path);
                        return (
                            <ListItemButton
                                key={item.path}
                                component={RouterLink}
                                to={item.path}
                                selected={selected}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // Bez minWidth:0 flex bērns nevar kļūt šaurāks par DataGrid saturu —
                    // logu sašaurinot vai drawer atverot lapa pārpildās pa labi.
                    minWidth: 0,
                    p: 3,
                    ml: drawerOpen ? 0 : `-${drawerWidth}px`,
                    transition: 'margin-left .2s',
                }}
            >
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
}
